import {
	type FrontendPlacementMessage,
	type FrontendStage,
} from '@post-office/message-maker-browser/types';

import { useCallback, useEffect, useState } from 'react';

import { noValidation } from '@post-office/message-maker-browser/message-helpers';

import {
	PlacementFrontend,
	type PlacementRenderProps,
} from '@post-office/browser--statuspage/statuspage-appearance-spotlight-placement';

import { useMountToHtmlElement } from '@post-office/components--post-office-team';

import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';

import { useMessageEvent } from '@post-office/message-lifecycle';

import { Text } from '@atlaskit/primitives/compiled';

import { FormattedMessage } from 'react-intl';

import { messages } from '../../../components/messages';

import { type MessageTemplateContext } from '../../../index';

import type { CreatePlacementMessageConfig } from '@post-office/message-template-contracts/placement-v2';

import { SpotlightPrimaryAction } from '@atlaskit/spotlight/primary-action';

import { SpotlightHeadline } from '@atlaskit/spotlight/headline';

import { SpotlightHeader } from '@atlaskit/spotlight/header';

import { SpotlightFooter } from '@atlaskit/spotlight/footer';

import { SpotlightDismissControl } from '@atlaskit/spotlight/dismiss-control';

import { SpotlightControls } from '@atlaskit/spotlight/controls';

import { SpotlightCard } from '@atlaskit/spotlight/card';

import { SpotlightBody } from '@atlaskit/spotlight/body';

import { SpotlightActions } from '@atlaskit/spotlight/actions';

import { PopoverTarget } from '@atlaskit/spotlight/popover-target';

import { PopoverProvider } from '@atlaskit/spotlight/popover-provider';

import { PopoverContent } from '@atlaskit/spotlight/popover-content';

type RenderProps = MessageTemplateContext & PlacementRenderProps;

/**
 * DOM selector for status-page to provide when integrating.
 * Targets the CSS view / appearance area.
 */
export const STATUSPAGE_APPEARANCE_SPOTLIGHT_SELECTOR = '#statuspage-appearance-spotlight-target';

const preventAutoDismiss = () => {};

export const render: {
	type: 'render';
	validate: (input: unknown) => RenderProps;
	Component: (input: PlacementRenderProps & Record<string, never>) => JSX.Element | null;
} = PlacementFrontend.createFrontendStage({
	type: 'render',
	validate: noValidation<RenderProps>(),
	Component: ({ onDismiss }: RenderProps) => {
		const { messageDismissed } = useMessageEvent();
		const { onClosed, onAcknowledged } = usePostOfficeUserInteractions();

		const [isVisible, setIsVisible] = useState<boolean>(false);

		useEffect(() => {
			if (typeof window === 'undefined') {
				return;
			}

			const checkForElement = () => {
				const element = globalThis.document?.querySelector(
					STATUSPAGE_APPEARANCE_SPOTLIGHT_SELECTOR,
				);
				if (element) {
					setIsVisible(true);
				}
			};

			// Check immediately
			checkForElement();

			// Also check periodically in case element isn't ready yet
			const intervalId = setInterval(checkForElement, 100);

			// Clean up after 5 seconds
			const timeoutId = setTimeout(() => {
				clearInterval(intervalId);
			}, 5000);

			return () => {
				clearInterval(intervalId);
				clearTimeout(timeoutId);
			};
		}, []);

		const renderInline = useMountToHtmlElement({
			selectors: STATUSPAGE_APPEARANCE_SPOTLIGHT_SELECTOR,
			as: 'inline',
			position: 'inside',
		});

		const dismiss = useCallback(async () => {
			await messageDismissed();
			void onClosed({
				interactionIdentifier: 'dismiss',
				additionalMetadata: {},
			});
			onDismiss?.();
		}, [messageDismissed, onClosed, onDismiss]);

		const handlePrimaryAction = useCallback(async () => {
			await messageDismissed();
			void onAcknowledged({
				interactionIdentifier: 'got-it',
				additionalMetadata: { actionTaken: true },
			});
			onDismiss?.();
		}, [messageDismissed, onAcknowledged, onDismiss]);

		if (!isVisible) {
			return null;
		}

		return (
			<div>
				{renderInline(
					<PopoverProvider>
						<PopoverTarget>
							<div />
						</PopoverTarget>
						<PopoverContent dismiss={preventAutoDismiss} placement="right-end" isVisible={true}>
							<SpotlightCard>
								<SpotlightHeader>
									<SpotlightHeadline>
										<FormattedMessage {...messages.heading} />
									</SpotlightHeadline>
									<SpotlightControls>
										<SpotlightDismissControl autoFocus={false} onClick={dismiss} />
									</SpotlightControls>
								</SpotlightHeader>
								<SpotlightBody>
									<Text>
										<FormattedMessage {...messages.body} />
									</Text>
								</SpotlightBody>
								<SpotlightFooter>
									<SpotlightActions>
										<SpotlightPrimaryAction onClick={handlePrimaryAction}>
											<FormattedMessage {...messages.primaryAction} />
										</SpotlightPrimaryAction>
									</SpotlightActions>
								</SpotlightFooter>
							</SpotlightCard>
						</PopoverContent>
					</PopoverProvider>,
				)}
			</div>
		);
	},
});

export const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
	render: FrontendStage<PlacementRenderProps>;
}> = PlacementFrontend.createMessageFrontend({
	render,
});
